import { useReducedMotion } from './useReducedMotion';
import { fadeSlideRTLVariants, fadeVariants } from '../animations/pageTransitions';
import { Variants } from 'framer-motion';

/**
 * Hook for managing page transitions
 *
 * Respects user's reduced motion preferences and provides
 * appropriate animation variants for page transitions.
 *
 * @param variant - Optional variant type ('fade', 'slide', etc.)
 * @returns Animation variants object for Framer Motion
 *
 * @example
 * const PageComponent = () => {
 *   const variants = usePageTransition();
 *   return (
 *     <motion.div
 *       variants={variants}
 *       initial="initial"
 *       animate="animate"
 *       exit="exit"
 *     >
 *       Content
 *     </motion.div>
 *   );
 * };
 */
export const usePageTransition = (variant: 'fade' | 'slide' = 'slide'): Variants => {
  const reducedMotion = useReducedMotion();

  // If user prefers reduced motion, return minimal fade animation
  if (reducedMotion) {
    return fadeVariants;
  }

  // Return appropriate variant based on type
  switch (variant) {
    case 'fade':
      return fadeVariants;
    case 'slide':
    default:
      return fadeSlideRTLVariants; // Use RTL-aware slide for Hebrew
  }
};
